
import type {
  CopyCompany,
  CopyProjectBody,
  CopyProjectResponse,
  CopyUser,
} from "@api/app-api";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoIcon from "@mui/icons-material/Info";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../api";
import { config, environmentLabels, type Environment } from "../../config";

// ============================================================================
// TYPES
// ============================================================================

// ============================================================================
// COPY PROJECT PAGE COMPONENT
// ============================================================================

export const CopyProjectPage: React.FC = () => {
  // Keyed by environment: only prod's portal reaches more than its own.
  const [companiesByEnv, setCompaniesByEnv] = useState<
    Partial<Record<Environment, CopyCompany[]>>
  >({});
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Users for source company (to copy UserProject from)
  const [sourceUsers, setSourceUsers] = useState<CopyUser[]>([]);
  const [loadingSourceUsers, setLoadingSourceUsers] = useState(false);

  // Users for target company
  const [targetUsers, setTargetUsers] = useState<CopyUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Form state
  const [sourceProjectId, setSourceProjectId] = useState("");
  const [sourceCompanyId, setSourceCompanyId] = useState("");
  const [sourceEnvironment, setSourceEnvironment] =
    useState<Environment>(config.safestEnvironment);
  const [sourceUserId, setSourceUserId] = useState<string | null>(null);
  const [targetCompanyId, setTargetCompanyId] = useState("");
  const [targetOwnerId, setTargetOwnerId] = useState("");
  const [targetEnvironment, setTargetEnvironment] =
    useState<Environment>(config.safestEnvironment);
  const [anonymize, setAnonymize] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [consentReference, setConsentReference] = useState("");
  // Policy "Use of Production Data in Non-Production Environments": out of prod only with the customer's written consent.
  const leavesProd = sourceEnvironment === "prod" && targetEnvironment !== "prod";

  // State
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<CopyProjectResponse | null>(null);

  // Each environment is committed and fails independently, so one outage cannot empty the others.
  const loadCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    setError(null);

    const failed: Environment[] = [];
    try {
      for (const env of config.copyEnvironments) {
        try {
          const result = await apiClient.getCopyCompanies(env);
          setCompaniesByEnv((current) => ({ ...current, [env]: result }));
        } catch (err) {
          console.error(`Failed to load ${env} companies:`, err);
          failed.push(env);
        }
      }
      if (failed.length > 0) {
        setError(`Could not load companies for: ${failed.join(", ")}`);
      }
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  const loadSourceUsers = useCallback(async () => {
    if (!sourceCompanyId || !sourceEnvironment) {
      setSourceUsers([]);
      return;
    }

    setLoadingSourceUsers(true);
    try {
      setSourceUsers(await apiClient.getCopyUsers(sourceEnvironment, sourceCompanyId));
    } catch (err) {
      console.error("Failed to load source users:", err);
      setSourceUsers([]);
    } finally {
      setLoadingSourceUsers(false);
    }
  }, [sourceCompanyId, sourceEnvironment]);

  // Load users for target company
  const loadTargetUsers = useCallback(async () => {
    if (!targetCompanyId || !targetEnvironment) {
      setTargetUsers([]);
      return;
    }

    setLoadingUsers(true);
    try {
      setTargetUsers(await apiClient.getCopyUsers(targetEnvironment, targetCompanyId));
    } catch (err) {
      console.error("Failed to load users:", err);
      setTargetUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [targetCompanyId, targetEnvironment]);

  // Load companies on mount
  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    loadSourceUsers();
  }, [loadSourceUsers]);

  useEffect(() => {
    loadTargetUsers();
  }, [loadTargetUsers]);

  useEffect(() => {
    setSourceUserId(null);
  }, [sourceCompanyId]);

  useEffect(() => {
    setTargetOwnerId("");
  }, [targetCompanyId]);

  const sourceCompanies = companiesByEnv[sourceEnvironment] ?? [];
  const targetCompanies = companiesByEnv[targetEnvironment] ?? [];

  // Handle copy
  const handleCopy = async () => {
    if (
      !sourceProjectId ||
      !sourceCompanyId ||
      !targetCompanyId ||
      !targetOwnerId
    ) {
      setError("Please fill in all required fields");
      return;
    }

    setCopying(true);
    setError(null);
    setSuccess(null);

    try {
      const data: CopyProjectBody = {
        sourceProjectId,
        sourceCompanyId,
        sourceEnvironment,
        sourceUserId: sourceUserId || undefined,
        targetCompanyId,
        targetOwnerId,
        targetEnvironment,
        anonymize,
        newProjectName: newProjectName || undefined,
        consentReference: leavesProd ? consentReference.trim() : undefined,
      };

      const result = await apiClient.copyProject(data);
      setSuccess(result);

      // Clear form on success
      setSourceProjectId("");
      setNewProjectName("");
      setConsentReference("");
    } catch (err) {
      console.error("Failed to copy project:", err);
      setError(err instanceof Error ? err.message : "Failed to copy project");
    } finally {
      setCopying(false);
    }
  };

  // Validation
  const isValid =
    sourceProjectId.trim() !== "" &&
    sourceCompanyId !== "" &&
    targetCompanyId !== "" &&
    targetOwnerId !== "" &&
    (!leavesProd || consentReference.trim() !== "");

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5">Copy Project</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Copy a business case between organizations and environments for
            analysis
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadCompanies}
          disabled={loadingCompanies}
        >
          Refresh Companies
        </Button>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }} icon={<InfoIcon />}>
        <Typography variant="body2">
          <strong>Security Note:</strong> You must know the exact project ID to
          copy it. This prevents unauthorized access to projects. The project ID
          can be found in the URL when viewing a project (e.g., /projects/
          <strong>abc123-def456-...</strong>).
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccess(null)}
        >
          <Typography variant="body2" fontWeight="bold">
            {success.message}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            New Project ID: <code>{success.newProjectId}</code>
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Copied: {success.details.metricsCount} metrics,{" "}
            {success.details.connectorsCount} connectors,{" "}
            {success.details.scenariosCount} scenarios,{" "}
            {success.details.decisionsCount} decisions,{" "}
            {success.details.goalsCount} goals,{" "}
            {success.details.investmentsCount} investments
          </Typography>
        </Alert>
      )}

      {loadingCompanies ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          {/* Source Configuration */}
          <Card sx={{ flex: "1 1 400px", minWidth: 350 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Source
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Where to copy the project from
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Source Environment</InputLabel>
                <Select
                  value={sourceEnvironment}
                  onChange={(e) => {
                    setSourceEnvironment(e.target.value as Environment);
                    setSourceCompanyId("");
                  }}
                  label="Source Environment"
                >
                  {config.copyEnvironments.map((environment) => (
                    <MenuItem key={environment} value={environment}>
                      {environmentLabels[environment].long}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Autocomplete
                options={sourceCompanies}
                getOptionLabel={(option) => option.name}
                value={
                  sourceCompanies.find((c) => c.id === sourceCompanyId) || null
                }
                onChange={(_e, value) => {
                  setSourceCompanyId(value?.id || "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Source Company"
                    helperText="Select the company that owns the project"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.activeUserCount} active users • {option.tier}
                      </Typography>
                    </Box>
                  </li>
                )}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Project ID"
                value={sourceProjectId}
                onChange={(e) => setSourceProjectId(e.target.value.trim())}
                placeholder="e.g., 550e8400-e29b-41d4-a716-446655440000"
                helperText="The UUID of the project to copy (found in the project URL)"
                sx={{ mb: 2 }}
              />

              <Autocomplete
                options={[
                  { id: null, label: "No user (skip UserProject)" },
                  ...sourceUsers.map((u) => ({
                    id: u.id,
                    label:
                      `${u.firstName || ""} ${u.lastName || ""} (${u.email})`.trim(),
                    user: u,
                  })),
                ]}
                getOptionLabel={(option) => option.label}
                value={
                  sourceUserId === null
                    ? { id: null, label: "No user (skip UserProject)" }
                    : sourceUsers.find((u) => u.id === sourceUserId)
                      ? {
                          id: sourceUserId,
                          label:
                            `${sourceUsers.find((u) => u.id === sourceUserId)?.firstName || ""} ${sourceUsers.find((u) => u.id === sourceUserId)?.lastName || ""} (${sourceUsers.find((u) => u.id === sourceUserId)?.email})`.trim(),
                          user: sourceUsers.find((u) => u.id === sourceUserId),
                        }
                      : { id: null, label: "No user (skip UserProject)" }
                }
                onChange={(_e, value) => {
                  setSourceUserId(value?.id || null);
                }}
                loading={loadingSourceUsers}
                disabled={!sourceCompanyId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Copy UserProject From"
                    helperText="Optionally copy the UserProject (viewport, chat history, etc.) from a specific user"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingSourceUsers ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id ?? "no-user"}>
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{
                          fontStyle: option.id === null ? "italic" : "normal",
                        }}
                      >
                        {option.id === null
                          ? "No user (skip UserProject)"
                          : `${(option as { user?: CopyUser }).user?.firstName || ""} ${(option as { user?: CopyUser }).user?.lastName || ""}`}
                      </Typography>
                      {option.id !== null && (
                        <Typography variant="caption" color="text.secondary">
                          {(option as { user?: CopyUser }).user
                            ?.email || ""}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
              />
            </CardContent>
          </Card>

          {/* Target Configuration */}
          <Card sx={{ flex: "1 1 400px", minWidth: 350 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Target
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Where to copy the project to
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Target Environment</InputLabel>
                <Select
                  value={targetEnvironment}
                  onChange={(e) => {
                    setTargetEnvironment(e.target.value as Environment);
                    setTargetCompanyId("");
                    setTargetOwnerId("");
                  }}
                  label="Target Environment"
                >
                  {config.copyEnvironments.map((environment) => (
                    <MenuItem key={environment} value={environment}>
                      {environmentLabels[environment].long}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Autocomplete
                options={targetCompanies}
                getOptionLabel={(option) => option.name}
                value={
                  targetCompanies.find((c) => c.id === targetCompanyId) || null
                }
                onChange={(_e, value) => {
                  setTargetCompanyId(value?.id || "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Target Company"
                    helperText="Select the destination company"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.activeUserCount} active users • {option.tier}
                      </Typography>
                    </Box>
                  </li>
                )}
                sx={{ mb: 2 }}
              />

              <Autocomplete
                options={targetUsers}
                getOptionLabel={(option) =>
                  `${option.firstName || ""} ${option.lastName || ""} (${option.email})`.trim()
                }
                value={targetUsers.find((u) => u.id === targetOwnerId) || null}
                onChange={(_e, value) => {
                  setTargetOwnerId(value?.id || "");
                }}
                loading={loadingUsers}
                disabled={!targetCompanyId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="New Owner"
                    helperText="Select who will own the copied project"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsers ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body1">
                        {option.firstName || ""} {option.lastName || ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    </Box>
                  </li>
                )}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="New Project Name (Optional)"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="Leave empty to keep original name"
                helperText="Optionally rename the copied project"
              />
            </CardContent>
          </Card>

          {/* Options */}
          <Card sx={{ flex: "1 1 100%", minWidth: 350 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Options
              </Typography>

              <Paper
                variant="outlined"
                sx={{ p: 2, mb: 2, bgcolor: "action.hover" }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={anonymize}
                      onChange={(e) => setAnonymize(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        Anonymize Project Data
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Replace all text with Lorem Ipsum and multiply all
                        numbers by a random factor (0.5x - 2x). Useful for
                        creating sample data or sharing for analysis without
                        revealing sensitive information.
                      </Typography>
                    </Box>
                  }
                />
              </Paper>

              {leavesProd && (
                <TextField
                  fullWidth
                  required
                  sx={{ mb: 2 }}
                  label="Customer consent reference"
                  value={consentReference}
                  onChange={(e) => setConsentReference(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                  helperText="Copying out of prod needs the customer's written consent. Enter its reference (e.g. email date and sender); it goes into the audit log."
                />
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Tooltip
                  title={
                    !isValid
                      ? "Please fill in all required fields"
                      : "Copy the project"
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={
                        copying ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <ContentCopyIcon />
                        )
                      }
                      onClick={handleCopy}
                      disabled={!isValid || copying}
                    >
                      {copying ? "Copying..." : "Copy Project"}
                    </Button>
                  </span>
                </Tooltip>

                <FormHelperText>
                  {anonymize
                    ? "Project will be anonymized during copy. Actuals sources are dropped."
                    : "Project will be copied as-is, except actuals sources — those are dropped so the copy never polls or emails."}
                </FormHelperText>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};
